import React, { useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  User,
  Settings as SettingsIcon,
  Shield,
  Lock,
  RefreshCw,
  QrCode,
  Save} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import { authApi } from '@domains/auth/api';
import PageInfoButton from '@/components/PageInfoButton';

const CompanySettings: React.FC = () => {
  const { user, hasPermission, isLoading: authLoading } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'company'>('profile');

  // Profile settings
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    profilePictureUrl: user?.profile_picture_url || ''
  });

  // Security settings
  const [security, setSecurity] = useState({
    twoFactorEnabled: user?.two_factor_enabled || false,
    sessionTimeout: 30
  });

  // 2FA setup state
  const [is2FASectionExpanded, setIs2FASectionExpanded] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
  const [twoFactorToken, setTwoFactorToken] = useState('');

  // Company settings
  const [company, setCompany] = useState({
    companyName: user?.company?.name || '',
    companyEmail: ''
  });

  const handleTwoFactorToggle = async (enabled: boolean) => {
    // If user is in the middle of setting up 2FA and clicks toggle again, just cancel setup
    if (!enabled && is2FASectionExpanded && qrCodeData !== null) {
      setIs2FASectionExpanded(false);
      setQrCodeData(null);
      setTwoFactorSecret(null);
      setTwoFactorToken('');
      setSecurity(prev => ({ ...prev, twoFactorEnabled: false }));
      return;
    }

    if (enabled) {
      // Optimistically update toggle state for visual feedback
      setSecurity(prev => ({ ...prev, twoFactorEnabled: true }));
      setIs2FASectionExpanded(true);
      try {
        const setupData = await authApi.setupTwoFactor();
        setQrCodeData(setupData.qrCode);
        setTwoFactorSecret(setupData.secret);
      } catch (error) {
        console.error('Failed to setup 2FA:', error);
        notify.error('Failed to setup 2FA, but you can try again');
        setSecurity(prev => ({ ...prev, twoFactorEnabled: false }));
        setIs2FASectionExpanded(false);
      }
    } else {
      // Close section immediately and disable 2FA
      setIs2FASectionExpanded(false);
      setQrCodeData(null);
      setTwoFactorSecret(null);
      setTwoFactorToken('');
      setSecurity(prev => ({ ...prev, twoFactorEnabled: false }));

      try {
        await authApi.disableTwoFactor();
        notify.success('Two-factor authentication disabled');
      } catch (error) {
        console.error('Failed to disable 2FA:', error);
        notify.error('Failed to disable 2FA');
        setSecurity(prev => ({ ...prev, twoFactorEnabled: true }));
        setIs2FASectionExpanded(true);
      }
    }
  };

  const handleConfirmTwoFactor = async () => {
    try {
      await authApi.enableTwoFactor(twoFactorToken);
      setSecurity(prev => ({ ...prev, twoFactorEnabled: true }));
      setQrCodeData(null);
      setTwoFactorSecret(null);
      setTwoFactorToken('');
      setIs2FASectionExpanded(false);
      notify.success('Two-factor authentication enabled');
    } catch (error) {
      notify.error('Invalid token. Please try again.');
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const res = await fetch(`/api/auth/users/${user?.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Failed'); }
      notify.success('Profile updated successfully!');
    } catch (error: any) {
      notify.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    try {
      setSaving(true);
      // Session timeout is a client-side preference — store in localStorage
      localStorage.setItem('sessionTimeout', String(security.sessionTimeout));
      notify.success('Security settings updated!');
    } catch (error) {
      notify.error('Failed to update security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    try {
      setSaving(true);
      const companyId = user?.company?.id || user?.companyId;
      if (!companyId) { notify.error('No company associated'); return; }
      const res = await fetch(`/api/companies/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: company.companyName,
          email: company.companyEmail,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Failed'); }
      notify.success('Company settings updated!');
    } catch (error: any) {
      notify.error(error.message || 'Failed to update company settings');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('users.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <Lock className="text-white" size={28} />
              <div>
                <h1 className="text-xl font-bold text-white">Access Denied</h1>
                <p className="text-red-100 mt-1 text-sm">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600 text-sm">You do not have permission to access account settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Company Settings</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500 mt-1">Manage your account and company preferences</p>
          </div>
          {saving && (
            <div className="flex items-center gap-2 text-blue-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Saving...</span>
            </div>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Profile</p>
                <p className="text-xl font-bold text-slate-900">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Shield className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Security</p>
                <p className="text-xl font-bold text-slate-900">{security.twoFactorEnabled ? 'Enabled' : 'Basic'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <SettingsIcon className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</p>
                <p className="text-xl font-bold text-slate-900">Configured</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden mb-6">
          <div className="flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-6 py-3 text-center font-semibold text-sm transition-colors ${
                activeTab === 'profile'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User size={16} />
                Profile
              </div>
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`flex-1 px-6 py-3 text-center font-semibold text-sm transition-colors ${
                activeTab === 'security'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Shield size={16} />
                Security
              </div>
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`flex-1 px-6 py-3 text-center font-semibold text-sm transition-colors ${
                activeTab === 'company'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <SettingsIcon size={16} />
                Company
              </div>
            </button>
          </div>
        </div>

        {/* Profile Settings */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">Profile Settings</h3>
              <p className="text-sm text-slate-500 mt-0.5">Update your personal information</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Enter your last name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors rounded-lg flex items-center gap-2"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">Security Settings</h3>
              <p className="text-sm text-slate-500 mt-0.5">Configure security preferences and authentication</p>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {/* Two-Factor Authentication */}
                <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Shield className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900">Two-Factor Authentication</h4>
                        <p className="text-xs text-slate-500">Add an extra layer of security to your account</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleTwoFactorToggle(!security.twoFactorEnabled)}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        security.twoFactorEnabled ? 'bg-blue-600' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                          security.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {is2FASectionExpanded && qrCodeData && (
                    <div className="mt-6 space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                        <div className="flex items-start gap-4">
                          <QrCode className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                          <div className="flex-1">
                            <h5 className="text-sm font-bold text-blue-900 mb-2">Scan QR Code</h5>
                            <p className="text-xs text-blue-700 mb-4">
                              Use your authenticator app to scan this QR code
                            </p>
                            <div className="bg-white p-4 rounded-lg inline-block">
                              <img
                                src={qrCodeData}
                                alt="2FA QR Code"
                                className="w-32 h-32"
                              />
                            </div>
                            {twoFactorSecret && (
                              <div className="mt-4">
                                <p className="text-xs text-blue-700 mb-2">Or enter this code manually:</p>
                                <code className="text-xs bg-blue-100 px-3 py-1.5 rounded font-mono text-blue-800">
                                  {twoFactorSecret}
                                </code>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Enter 6-digit code from your authenticator app
                        </label>
                        <input
                          type="text"
                          value={twoFactorToken}
                          onChange={(e) => setTwoFactorToken(e.target.value)}
                          placeholder="123456"
                          maxLength={6}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg tracking-widest font-mono"
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => handleTwoFactorToggle(false)}
                          className="px-5 py-2.5 border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmTwoFactor}
                          disabled={twoFactorToken.length !== 6}
                          className="flex-1 px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors rounded-lg flex items-center justify-center gap-2"
                        >
                          <Shield size={16} />
                          Enable 2FA
                        </button>
                      </div>
                    </div>
                  )}

                  {security.twoFactorEnabled && !is2FASectionExpanded && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                      <Shield className="w-4 h-4" />
                      <span className="font-semibold">Two-factor authentication is enabled</span>
                    </div>
                  )}
                </div>

                {/* Session Timeout */}
                <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                      <RefreshCw className="text-amber-600" size={20} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">Session Management</h4>
                      <p className="text-xs text-slate-500">Configure automatic logout settings</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Session Timeout (minutes)
                    </label>
                    <input
                      type="number"
                      value={security.sessionTimeout}
                      onChange={(e) => setSecurity(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) || 30 }))}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      min="5"
                      max="120"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      You will be automatically logged out after this period of inactivity
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveSecurity}
                  disabled={saving}
                  className="px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors rounded-lg flex items-center gap-2"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Security'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company Settings */}
        {activeTab === 'company' && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">Company Settings</h3>
              <p className="text-sm text-slate-500 mt-0.5">Manage company information and preferences</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={company.companyName}
                    onChange={(e) => setCompany({ ...company, companyName: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Email</label>
                  <input
                    type="email"
                    value={company.companyEmail}
                    onChange={(e) => setCompany({ ...company, companyEmail: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Enter company email"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveCompany}
                  disabled={saving}
                  className="px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors rounded-lg flex items-center gap-2"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Company'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySettings;
